import { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { AppContext } from '../../../contexts/AppContext';
import api from '../../../services/api';
import {
  Grid,
  Button,
  Paper,
  TextField,
  Text,
  Divider,
  List,
  ListItem,
  IconButton,
  Chip
} from '../../core';
import { SeccionHeader, ConfirmDialog, AlertDialog, UserSelect } from '../../patterns';
import { NotificationNav } from '../../patterns/NotificationNav';
import { Delete as DeleteIcon, Add as AddIcon } from '@material-ui/icons';

const emptyUserGroup = () => ({
  name: '',
  description: '',
  users: []
});

export default function UserGroupEdit() {
  const { setLoading } = useContext(AppContext);
  const { id } = useParams();
  const history = useHistory();

  const [usergroup, setUserGroup] = useState(emptyUserGroup());
  const [state, setState] = useState({
    title: id === 'new' ? 'Novo Grupo de Usuários' : 'Grupo de Usuários',
    showConfirmDialog: false,
    showUserSelect: false,
    errors: {}
  });

  const [alert, setAlert] = useState({
    show: false,
    errors: [{ message: 'Ocorreu um erro' }]
  });

  useEffect(() => {
    if (id !== 'new') {
      setLoading(true);
      api
        .get(`/admin/user-groups/${id}`)
        .then((res) => {
          setLoading(false);
          setUserGroup(res.data.usergroup);
          setState((prev) => ({
            ...prev,
            title: res.data.usergroup.name
          }));
        })
        .catch((err) => {
          setAlert({
            errors: err.response?.data?.errors || [{ message: 'Erro ao carregar grupo de usuários' }],
            show: true
          });
          setLoading(false);
          history.push('/app/notifications/user-groups');
        });
    }
  }, [id, setLoading, history]);

  const handleChange = ({ target }) => {
    const { name, value } = target;
    setUserGroup((prev) => ({ ...prev, [name]: value }));
    setState((prev) => {
      const newErrors = { ...prev.errors };
      delete newErrors[name];
      return { ...prev, errors: newErrors };
    });
  };

  const handleDeleteButton = () => {
    api
      .delete(`/admin/user-groups/${usergroup.id}`)
      .then(() => history.push('/app/notifications/user-groups'))
      .catch((err) => {
        setAlert({
          errors: err.response?.data?.errors || [{ message: 'Erro ao excluir grupo' }],
          show: true
        });
      });
  };

  const handleCancelButton = () => {
    history.push('/app/notifications/user-groups');
  };

  const validateFields = () => {
    const errors = {};
    if (!usergroup.name?.trim()) {
      errors.name = 'Nome é obrigatório';
    }
    return errors;
  };

  const handleSaveButton = () => {
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      setState((prev) => ({ ...prev, errors: validationErrors }));
      setAlert({
        show: true,
        errors: [{ message: 'Por favor, preencha todos os campos obrigatórios' }]
      });
      return;
    }

    const payload = {
      usergroup: {
        name: usergroup.name?.trim(),
        description: usergroup.description?.trim() || undefined,
        users: (usergroup.users || []).map((u) => u.id || u._id)
      }
    };

    const request = usergroup.id
      ? api.patch(`/admin/user-groups/${usergroup.id}`, payload)
      : api.post('/admin/user-groups', payload);

    setLoading(true);
    request
      .then(() => {
        setLoading(false);
        history.push('/app/notifications/user-groups');
      })
      .catch((err) => {
        setLoading(false);
        setAlert({
          errors: err.response?.data?.errors || [{ message: 'Erro ao salvar grupo' }],
          show: true
        });
      });
  };

  const handleUserSelected = (selection) => {
    const newUsers = Array.isArray(selection) ? selection : [selection];
    setUserGroup((prev) => {
      const existingIds = new Set((prev.users || []).map((u) => u.id || u._id));
      const toAdd = newUsers.filter((u) => !existingIds.has(u.id || u._id));
      return { ...prev, users: [...(prev.users || []), ...toAdd] };
    });
    setState((prev) => ({ ...prev, showUserSelect: false }));
  };

  const handleRemoveUser = (userId) => {
    setUserGroup((prev) => ({
      ...prev,
      users: (prev.users || []).filter((u) => (u.id || u._id) !== userId)
    }));
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <NotificationNav />
        </Grid>
        <SeccionHeader
          title={state.title}
          deleteButtonAction={() => setState({ ...state, showConfirmDialog: true })}
          deleteButton={!!usergroup.id}
        />
        <AlertDialog
          open={alert.show}
          handleClose={() => setAlert({ ...alert, show: false })}
          errors={alert.errors}
        />

        <Grid item xs={12}>
          <Paper style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 20 }}>
            <TextField
              label="Nome *"
              variant="outlined"
              name="name"
              value={usergroup.name || ''}
              fullWidth
              error={state.errors.hasOwnProperty('name')}
              helperText={state.errors.name}
              onChange={handleChange}
              style={{ marginBottom: 20 }}
            />
            <TextField
              label="Descrição"
              variant="outlined"
              name="description"
              value={usergroup.description || ''}
              fullWidth
              multiline
              rows={2}
              onChange={handleChange}
              style={{ marginBottom: 20 }}
            />

            <Divider style={{ margin: '16px 0' }} />
            <Text variant="h6">Membros ({usergroup.users?.length || 0})</Text>

            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setState((prev) => ({ ...prev, showUserSelect: true }))}
              style={{ marginTop: 12, marginBottom: 16, alignSelf: 'flex-start' }}
            >
              Adicionar usuário
            </Button>

            <List>
              {(usergroup.users || []).map((user) => (
                <ListItem
                  key={user.id || user._id}
                  style={{
                    backgroundColor: '#f5f5f5',
                    borderRadius: 4,
                    marginBottom: 4
                  }}
                >
                  <Chip label={user.name || user.email || user.id} />
                  <IconButton
                    onClick={() => handleRemoveUser(user.id || user._id)}
                    color="secondary"
                    style={{ marginLeft: 'auto' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
              <Button
                style={{ marginRight: 10 }}
                variant="outlined"
                color="primary"
                onClick={handleCancelButton}
              >
                Cancelar
              </Button>
              <Button variant="contained" color="primary" onClick={handleSaveButton}>
                Salvar
              </Button>
            </div>
          </Paper>
        </Grid>
      </Grid>

      {state.showConfirmDialog && (
        <ConfirmDialog
          open={state.showConfirmDialog}
          title="Excluir grupo"
          question={`Deseja realmente excluir o grupo '${usergroup.name}'?`}
          handleConfirm={handleDeleteButton}
          handleClose={() => setState({ ...state, showConfirmDialog: false })}
          cancelButton
        />
      )}

      {state.showUserSelect && (
        <UserSelect
          open={state.showUserSelect}
          multiSelect
          handleClose={() => setState((prev) => ({ ...prev, showUserSelect: false }))}
          handleSelected={handleUserSelected}
          labelConfirmButton="Adicionar"
        />
      )}
    </>
  );
}
